import {
    IntersectionRuleTestResult,
    IntersectionXRuleTestResult,
    IntersectionYRuleTestResult,
} from "./IntersectionRuleTestResult";
import { IConfigStorage } from "../../Common/IConfigStorage";
import { ExpressionConfiguration } from "../../Expressions/ExpressionConfiguration";
import { ExpressionValueType } from "../../Expressions/ExpressionValueType";
import { IDesignValidationProvider, ValidationIssue } from "../../Common/IDesignValidationProvider";
import { ISameResultTypeRule } from "../ISameResultTypeRule";
import { RuleExecutable } from "../../Common/Execution/RuleExecutable";
import { DataUtilities } from "../../Common/DataUtilities";
import { IDataDefinition } from "../../Common/Data/Definitions/IDataDefinition";
import { BooleanDataDefinition } from "../../Common/Data/Definitions/BooleanDataDefinition";
import { ITestingContext } from "../../Common/Testing/ITestingContext";
import { ExecutionData } from "../../Common/Data/ExecutionData/ExecutionData";
import { EventLog } from "../../Common/Event/EventLog";
import type { AppGlueAny } from "../../Common/AppGlueAny";

export class IntersectionResult implements IConfigStorage {
    x: string = "";
    y: string = "";
    resultExpression: ExpressionConfiguration = new ExpressionConfiguration();

    setValue(newValue: ExpressionConfiguration | AppGlueAny) {
        if (!(newValue instanceof ExpressionConfiguration)) {
            this.resultExpression.valueType = ExpressionValueType.VALUE;
            this.resultExpression.value = newValue;
        }
    }

    getStorageData() {
        let obj = {};

        Reflect.set(obj, "x", this.x);
        Reflect.set(obj, "y", this.y);

        Reflect.set(obj, "resultExpression", this.resultExpression.getStorageData());

        return obj;
    }

    setStorageData(data: object) {
        this.x = Reflect.get(data, "x");
        this.y = Reflect.get(data, "y");

        let resultExpression = Reflect.get(data, "resultExpression");

        this.resultExpression.setStorageData(resultExpression);
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        if (this.x === "") {
            issues.push(new ValidationIssue("invalid x"));
        }

        if (this.y === "") {
            issues.push(new ValidationIssue("invalid y"));
        }

        issues.push(...this.resultExpression.getDesignValidationIssues());

        return issues;
    }
}

export class IntersectionRuleConfiguration
    extends RuleExecutable
    implements IConfigStorage, ISameResultTypeRule, IDesignValidationProvider
{
    _id: string = DataUtilities.generateUniqueId();
    title?: string;

    constructor() {
        super();

        this.addXAxisExpression();
        this.addYAxisExpression();
    }

    private _xaxisExpressions: ExpressionConfiguration[] = [];

    get xaxisExpressions() {
        return this._xaxisExpressions;
    }

    private _yaxisExpressions: ExpressionConfiguration[] = [];

    get yaxisExpressions() {
        return this._yaxisExpressions;
    }

    private _results: IntersectionResult[] = [];

    get results() {
        return this._results;
    }

    private _resultType: IDataDefinition = new BooleanDataDefinition();

    get resultType() {
        return this._resultType;
    }

    removeExpression(exp: ExpressionConfiguration, direction: "x" | "y" = "x") {
        if (direction === "x") this.removeXAxisExpression(exp);
        if (direction === "y") this.removeYAxisExpression(exp);
    }

    removeXAxisExpression(exp: ExpressionConfiguration) {
        if (this._xaxisExpressions.length === 1) return;
        this._xaxisExpressions = this._xaxisExpressions.filter((e) => e._id !== exp._id);

        this._results = this._results.filter((result) => result.x !== exp._id);
    }

    removeYAxisExpression(exp: ExpressionConfiguration) {
        if (this._yaxisExpressions.length === 1) return;
        this._yaxisExpressions = this._yaxisExpressions.filter((e) => e._id !== exp._id);

        this._results = this._results.filter((result) => result.y !== exp._id);
    }

    addYAxisExpression(exp?: ExpressionConfiguration) {
        let expression = exp;

        if (!expression) {
            expression = new ExpressionConfiguration();
            expression.valueType = ExpressionValueType.SUBEXPRESSION;
        }

        expression.inputData = () => {
            return this.getInputDefinition();
        };

        this._yaxisExpressions.push(expression);

        for (let x of this.xaxisExpressions) {
            this.results.push(this.createResultCell(x, expression));
        }
    }

    addXAxisExpression(exp?: ExpressionConfiguration) {
        let expression = exp;

        if (!expression) {
            expression = new ExpressionConfiguration();
            expression.valueType = ExpressionValueType.SUBEXPRESSION;
        }
        expression.inputData = () => this.getInputDefinition();

        expression.executionResult = () => {
            return new BooleanDataDefinition();
        };

        this._xaxisExpressions.push(expression);

        for (let y of this.yaxisExpressions) {
            this.results.push(this.createResultCell(expression, y));
        }
    }

    createResultCell(x: ExpressionConfiguration, y: ExpressionConfiguration) {
        let resultCol = new IntersectionResult();
        resultCol.x = x._id;
        resultCol.y = y._id;
        resultCol.resultExpression.executionResult = () => {
            return this.resultType;
        };
        resultCol.resultExpression.inputData = () => {
            return this.getInputDefinition();
        };

        resultCol.resultExpression.expressionRoot.expectedType = this.resultType;
        return resultCol;
    }

    validateResultCells() {
        // makes sure that result cells have all required
    }

    purgeResultCells() {
        // remove unused result cells
    }

    run(data: ExecutionData, testContext?: ITestingContext): AppGlueAny {
        let subContext: IntersectionRuleTestResult | undefined;
        let ysubContext: IntersectionYRuleTestResult | undefined;
        let xsubContext: IntersectionXRuleTestResult | undefined;

        if (testContext) {
            subContext = new IntersectionRuleTestResult(this._id);

            subContext.definition = this;

            subContext.addLogItem(new EventLog("IntersectionRule: start evaluating"));
            testContext.addTestResultItem(subContext);

            ysubContext = new IntersectionYRuleTestResult(`${this._id}_y`);
            subContext.addTestResultItem(ysubContext);

            xsubContext = new IntersectionXRuleTestResult(`${this._id}_x`);
            subContext.addTestResultItem(xsubContext);
        }

        let evalutatedYID: string | undefined;
        let evalutatedXID: string | undefined;

        for (let yIdx in this._yaxisExpressions) {
            let y = this._yaxisExpressions[yIdx];
            try {
                if (ysubContext) {
                    ysubContext.addLogItem(
                        new EventLog(`IntersectionRule: start evalutating ${parseInt(yIdx, 10) + 1}th y expression`),
                    );
                }
                let res = y.run(data, ysubContext);

                if (ysubContext) {
                    ysubContext.addLogItem(
                        new EventLog(
                            `IntersectionRule: end evalutating ${
                                parseInt(yIdx, 10) + 1
                            }th y expression with result [${res}]`,
                        ),
                    );
                }

                if (res) {
                    evalutatedYID = y._id;
                    break;
                }
            } catch (e) {
                if (ysubContext) {
                    ysubContext.isError = true;
                    ysubContext.result = e.message;

                    ysubContext.addLogItem(new EventLog(`IntersectionRule: end evaluating with error [${e.message}]`));

                    if (subContext) {
                        subContext.isError = true;
                        subContext.result = e.message;

                        subContext.addLogItem(
                            new EventLog(`IntersectionRule: end evaluating with error [${e.message}]`),
                        );
                    }
                }

                throw e;
            }
        }

        for (let xIdx in this._xaxisExpressions) {
            let x = this._xaxisExpressions[xIdx];
            try {
                let res = x.run(data, xsubContext);

                if (res) {
                    evalutatedXID = x._id;
                    break;
                }
            } catch (e) {
                if (xsubContext) {
                    xsubContext.isError = true;
                    xsubContext.result = e.message;

                    xsubContext.addLogItem(new EventLog(`IntersectionRule: end evaluating with error [${e.message}]`));
                    if (subContext) {
                        subContext.isError = true;
                        subContext.result = e.message;

                        subContext.addLogItem(
                            new EventLog(`IntersectionRule: end evaluating with error [${e.message}]`),
                        );
                    }
                }

                throw e;
            }
        }

        if (evalutatedXID && evalutatedYID) {
            let resultCell = this.findResultCellByXY(evalutatedXID, evalutatedYID);

            if (!resultCell) {
                throw new Error("Can not find a result cell");
            }
            if (subContext) {
                subContext.addLogItem(new EventLog("IntersectionRule: start evalutating result expression"));
            }

            try {
                let res = resultCell.resultExpression.run(data, subContext);

                if (subContext) {
                    subContext.addLogItem(
                        new EventLog(`IntersectionRule: end evalutating result expression with result [${res}]`),
                    );
                    subContext.result = res.toString();
                }

                return res;
            } catch (e) {
                if (subContext) {
                    subContext.isError = true;
                    subContext.result = e.message;
                    subContext.addLogItem(new EventLog(`IntersectionRule: end evaluating with error [${e.message}]`));
                }

                throw e;
            }
        }
    }

    findResultCellByXY(x: string, y: string): IntersectionResult | undefined {
        return this.results.find((res) => res.x === x && res.y === y);
    }

    setTypeResults(newType: IDataDefinition) {
        if (!this.canChangeOutputs) return;

        this._resultType = newType;
        for (let resCol of this.results) {
            resCol.resultExpression.executionResult = () => this.resultType;
        }
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        for (let x in this.xaxisExpressions) {
            let xexp = this.xaxisExpressions[x];
            issues.push(
                ...xexp.getDesignValidationIssues().map((issue) => {
                    issue.issue = `Cell Expression[${parseInt(x, 10) + 1}]:: ${issue.issue}`;
                    return issue;
                }),
            );
        }

        for (let y in this.yaxisExpressions) {
            let yexp = this.yaxisExpressions[y];
            issues.push(
                ...yexp.getDesignValidationIssues().map((issue) => {
                    issue.issue = `Row Expression[${parseInt(y, 10) + 1}]:: ${issue.issue}`;
                    return issue;
                }),
            );
        }

        for (let y in this.yaxisExpressions) {
            let yexp = this.yaxisExpressions[y];
            for (let x in this.xaxisExpressions) {
                let xexp = this.xaxisExpressions[x];

                const result = this.findResultCellByXY(xexp._id, yexp._id);
                if (!result) {
                    // issues.push(
                    //     new ValidationIssue(`Result Cell[${parseInt(y, 10) + 1}, ${parseInt(x, 10) + 1}] is required.`)
                    // );
                } else {
                    issues.push(
                        ...result.getDesignValidationIssues().map((issue) => {
                            issue.issue = `Result Cell[${parseInt(y, 10) + 1}, ${parseInt(x, 10) + 1}]:: ${
                                issue.issue
                            }`;

                            return issue;
                        }),
                    );
                }
            }
        }

        return issues;
    }

    getStorageData() {
        let obj = {};

        let xaxisExpressions: object[] = [];
        for (let x of this.xaxisExpressions) {
            xaxisExpressions.push(x.getStorageData());
        }
        Reflect.set(obj, "xaxisExpressions", xaxisExpressions);

        let yaxisExpressions: object[] = [];
        for (let y of this.yaxisExpressions) {
            yaxisExpressions.push(y.getStorageData());
        }
        Reflect.set(obj, "yaxisExpressions", yaxisExpressions);

        let results: object[] = [];
        for (let rescol of this.results) {
            results.push(rescol.getStorageData());
        }
        Reflect.set(obj, "results", results);

        return obj;
    }

    setStorageData(data: object) {
        this._xaxisExpressions = (Reflect.get(data, "xaxisExpressions") || []).map((datum: object) => {
            let exp = new ExpressionConfiguration();
            exp.setStorageData(datum);

            return exp;
        });

        this._yaxisExpressions = (Reflect.get(data, "yaxisExpressions") || []).map((datum: object) => {
            let exp = new ExpressionConfiguration();
            exp.setStorageData(datum);

            return exp;
        });

        this._results = (Reflect.get(data, "results") || []).map((datum: object) => {
            let res = new IntersectionResult();
            res.setStorageData(datum);

            return res;
        });
    }
}
